import type { ApplicationInsights } from '@microsoft/applicationinsights-web';
import { config } from './config';

const connectionString = config.appInsightsConnectionString;

let _ai: ApplicationInsights | null = null;

// Lazy-loaded so the SDK (~190KB raw / ~77KB gzip) is fetched after first paint,
// keeping the initial bundle under the 200KB performance budget per
// frontend-performance.md.
export async function initAppInsights(): Promise<ApplicationInsights | null> {
  if (_ai) return _ai;
  if (!connectionString) return null;

  const { ApplicationInsights } = await import('@microsoft/applicationinsights-web');
  _ai = new ApplicationInsights({
    config: {
      connectionString,
      enableAutoRouteTracking: true,
      autoTrackPageVisitTime: true,
      enableCorsCorrelation: true,
      enableRequestHeaderTracking: true,
      enableResponseHeaderTracking: true,
      enableUnhandledPromiseRejectionTracking: true,
      disableFetchTracking: false,
    },
  });

  _ai.loadAppInsights();
  _ai.addTelemetryInitializer((envelope) => {
    if (envelope.tags) envelope.tags['ai.cloud.role'] = 'client-portal';
    if (envelope.data?.baseData) envelope.data.baseData.properties = {
      ...envelope.data.baseData.properties,
      portal: 'client',
    };
  });
  _ai.trackPageView();

  // Expose the instance globally so non-React surfaces — the 404/500 pages
  // rendered via the SPA fallback (NotFoundPage / ServerErrorPage) — can emit
  // telemetry via window.appInsights.trackEvent / trackException. Set only
  // after init() runs, which the consent gate (CookieConsentMount) calls solely
  // once 'analytics' is accepted; stopAppInsights() clears it on withdrawal.
  // Without this the pages' window.appInsights?.trackEvent calls are dead.
  if (typeof window !== 'undefined') {
    (window as unknown as { appInsights?: ApplicationInsights }).appInsights = _ai;
  }

  return _ai;
}

export function getAppInsights(): ApplicationInsights | null {
  return _ai;
}

/**
 * Tear down Application Insights — used when the visitor withdraws analytics
 * consent. Flushes + unloads the SDK and clears the instance so a later
 * re-consent re-initialises cleanly. No-op if AI was never started.
 */
export function stopAppInsights(): void {
  if (!_ai) return;
  try {
    _ai.unload(false);
  } catch {
    // best-effort teardown — ignore SDK unload errors
  }
  _ai = null;
  // Remove the global so the error pages stop emitting after consent withdrawal.
  if (typeof window !== 'undefined') {
    (window as unknown as { appInsights?: ApplicationInsights }).appInsights = undefined;
  }
}
